// Globals
import { useTranslation } from 'react-i18next';

// Components
import { FormItem } from '@/components';
import { Button, PasswordInput } from '@/ui';
import { StyledForm } from './ChangePasswordContainerStyles';

import { useAppDispatch, useAppState } from '@/store';
import { changePassword, selectAuthIsLoading } from '../../feature';

// Helpers
import { AuthFieldsNames, TChangePasswordFormValues } from '../../helpers/types';
import { appCookiesStorage } from '@/utils';
import { REFRESH_TOKEN_STORAGE_KEY } from '@/constants/common';
import { useGetFormValidationRules } from '../../hooks';

interface TChangePasswordContainerProps {
  onSubmit?: (values: TChangePasswordFormValues) => void;
}

const ChangePasswordContainer = ({ onSubmit }: TChangePasswordContainerProps) => {
  const { t } = useTranslation(['auth']);
  const validationRules = useGetFormValidationRules();
  const dispatch = useAppDispatch();
  const isLoading = useAppState(selectAuthIsLoading);

  const handleSubmit = (values: TChangePasswordFormValues) => {
    dispatch(
      changePassword({
        newPassword: values[AuthFieldsNames.NEW_PASSWORD],
        oldPassword: values[AuthFieldsNames.CURRENT_PASSWORD],
        token: appCookiesStorage.getItem(REFRESH_TOKEN_STORAGE_KEY) ?? '',
      }),
    );
    onSubmit?.(values);
  };

  return (
    <StyledForm onFinish={handleSubmit}>
      <FormItem
        name={AuthFieldsNames.CURRENT_PASSWORD}
        rules={validationRules[AuthFieldsNames.NEW_PASSWORD]}
        label={t('change_password_current')}
      >
        <PasswordInput size="large" />
      </FormItem>
      <FormItem
        name={AuthFieldsNames.PASSWORD}
        rules={validationRules[AuthFieldsNames.NEW_PASSWORD]}
        label={t('change_password_new')}
      >
        <PasswordInput size="large" />
      </FormItem>
      <FormItem
        name={AuthFieldsNames.NEW_PASSWORD}
        rules={[
          ...validationRules[AuthFieldsNames.NEW_PASSWORD],
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue(AuthFieldsNames.PASSWORD) === value) {
                return Promise.resolve();
              }

              return Promise.reject(t('validation_message_confirm_password', { ns: 'common' }));
            },
          }),
        ]}
        label={t('change_password_repeat')}
      >
        <PasswordInput size="large" />
      </FormItem>
      <Button block shape="round" type="primary" htmlType={'submit'} loading={isLoading}>
        {t('auth_reset_password_save')}
      </Button>
    </StyledForm>
  );
};

export default ChangePasswordContainer;
